import { ItemAddonBehaviour, ItemAddonType } from '@order-mage/integration-sdk';
import { MAX_ADDON_NESTING_DEPTH } from '@/api/menu-list/max-addon-nesting-depth';
import type {
    StorefrontAddonDef,
    StorefrontPackageBehaviour,
    StorefrontPackageItem,
} from '@/api/menu-list/storefront-menu-snapshot.types';
import type { CartMenuItem, CartPackageRef, MenuListsData } from '@/entities/cart/lib/item-price';
import {
    getContainedSinglePickHash,
    getHashFromItemIdMap,
    getMemberHash,
    normalizeAddonQty,
} from '@/entities/cart/lib/pricing';
import { Language } from '@/shared/model/Language';

type PackageWithItem = CartPackageRef & { packageItem?: StorefrontPackageItem };

export type PackageItemByIdMap = Map<string, CartMenuItem>;

export const buildPackageItemByIdMap = (
    menuLists: Array<{ itemRefs: Array<{ item?: CartMenuItem }> }> | null | undefined,
): PackageItemByIdMap => {
    const map: PackageItemByIdMap = new Map();
    if (!menuLists) return map;
    for (const menuList of menuLists) {
        for (const ref of menuList.itemRefs) {
            if (ref.item?.id) map.set(ref.item.id, ref.item);
        }
    }
    return map;
};

export const enrichPackageWithPriceableItem = <T extends PackageWithItem>(
    pkg: T,
    itemById: PackageItemByIdMap | Array<{ item?: CartMenuItem }>,
): T => {
    const embedded = pkg.packageItem;
    const embeddedHasPrices = (embedded?.prices?.length ?? 0) > 0;

    if (embedded && embeddedHasPrices) {
        return { ...pkg, packageItem: embedded };
    }

    const fromRefs =
        itemById instanceof Map
            ? itemById.get(pkg.packageItemId)
            : itemById.find((ref) => ref.item?.id === pkg.packageItemId)?.item;
    const packageItem = fromRefs
        ? {
              id: fromRefs.id,
              name: fromRefs.name,
              trait: fromRefs.trait,
              prices: fromRefs.prices,
              packageBehaviours: fromRefs.packageBehaviours,
          }
        : embedded;

    return { ...pkg, ...(packageItem ? { packageItem } : {}) };
};

export const isPackageApplicable = (
    pkg: CartPackageRef,
    deliveryMethodDefinitionId: string | undefined,
    userLanguage?: Language,
): boolean => {
    if (!deliveryMethodDefinitionId) return true;

    const behaviour = pkg.packageItem?.packageBehaviours?.[0];
    const hasNoBehaviour = !behaviour;
    const allowsAllDeliveryMethods = behaviour?.allowedDeliveryMethodsNull;
    const isInAllowedList = behaviour?.allowedDeliveryMethods?.some(
        (dmId) => dmId === deliveryMethodDefinitionId,
    );

    const matchesDeliveryMethod = hasNoBehaviour || allowsAllDeliveryMethods || !!isInAllowedList;
    if (!matchesDeliveryMethod) return false;

    if (userLanguage && pkg.packageItem) {
        if (!pkg.packageItem.name?.[userLanguage]) return false;
    }

    return true;
};

export const shouldIncludePackages = (
    item: { packages?: CartPackageRef[]; packageBehaviours?: StorefrontPackageBehaviour[] },
    deliveryMethodDefinitionId: string | undefined,
): boolean => {
    if (!item.packageBehaviours) return true;
    if (!deliveryMethodDefinitionId) return true;

    for (const behaviour of item.packageBehaviours) {
        if (
            behaviour.allowedDeliveryMethodsNull === false &&
            !behaviour.allowedDeliveryMethods.includes(deliveryMethodDefinitionId)
        ) {
            return false;
        }
    }
    return true;
};

export type EnrichedPackageWithQuantity = CartPackageRef & {
    addonQuantity: number;
};

type CartAddons = Record<string, string | Record<string, number | boolean>> | undefined;

export const collectAddonMemberPackages = (
    cartAddons: CartAddons,
    menuListsData: MenuListsData | undefined,
    deliveryMethodDefinitionId: string | undefined,
    userLanguage: Language,
    /** Reuse across cart lines — built once in computeCartTotals when omitted. */
    packageItemById?: PackageItemByIdMap,
    context?: {
        addons: StorefrontAddonDef[] | null | undefined;
        memberVariants?: Record<string, string>;
    },
): EnrichedPackageWithQuantity[] => {
    if (!cartAddons || !menuListsData?.items) return [];

    const itemById = packageItemById ?? buildPackageItemByIdMap(menuListsData.menuLists);
    const collected: EnrichedPackageWithQuantity[] = [];
    const idToHash = getHashFromItemIdMap(menuListsData.items);
    const addPackages = (item: CartMenuItem | undefined, quantity: number) => {
        if (!item || quantity === 0 || !shouldIncludePackages(item, deliveryMethodDefinitionId)) return;
        for (const pkg of item.packages ?? []) {
            const enriched = enrichPackageWithPriceableItem(pkg, itemById);
            if (!isPackageApplicable(enriched, deliveryMethodDefinitionId, userLanguage)) continue;
            collected.push({ ...enriched, addonQuantity: quantity });
        }
    };
    const walk = (defs: StorefrontAddonDef[] | null | undefined, depth: number, path: Set<string>) => {
        if (depth >= MAX_ADDON_NESTING_DEPTH) return;
        for (const addon of defs ?? []) {
            if (addon.type === ItemAddonType.GROUP) {
                walk(addon.groupMembers, depth, path);
                continue;
            }
            const single = addon.behaviour === ItemAddonBehaviour.SINGLE_PICK;
            const contained = single ? getContainedSinglePickHash(addon.members, idToHash) : undefined;
            const value = cartAddons[addon.id];
            const singleHash = typeof value === 'string' ? value : depth > 0 ? contained : undefined;
            for (const member of addon.members) {
                const memberHash = getMemberHash(member, idToHash);
                const item = memberHash ? menuListsData.items[memberHash]?.item : undefined;
                if (!memberHash || !item?.id || path.has(item.id)) continue;
                const containedAmount = Number(member.amount?.value ?? 0);
                const quantities = typeof value === 'object' && value !== null ? value : undefined;
                const quantity = single
                    ? singleHash === memberHash
                        ? 1
                        : 0
                    : quantities && memberHash in quantities
                      ? normalizeAddonQty(quantities[memberHash])
                      : depth > 0
                        ? containedAmount
                        : 0;
                if (quantity <= 0) continue;
                const chargeQty = single
                    ? memberHash === contained
                        ? 0
                        : 1
                    : Math.max(0, quantity - containedAmount);
                const variant = item.variantRows?.find(
                    (row) => row.id === context?.memberVariants?.[item.id] && !row.isDefault,
                );
                const variantHash = variant ? idToHash.get(variant.itemId) : undefined;
                const variantItem = variantHash ? menuListsData.items[variantHash]?.item : undefined;
                addPackages(variantItem ?? item, chargeQty);
                if (variantItem) {
                    const keptQuantity = quantity - chargeQty;
                    const defaultRow = item.variantRows?.find((row) => row.isDefault);
                    const defaultHash = defaultRow ? idToHash.get(defaultRow.itemId) : undefined;
                    const defaultItem = defaultHash ? menuListsData.items[defaultHash]?.item : undefined;
                    addPackages(variantItem, keptQuantity);
                    addPackages(defaultItem, -keptQuantity);
                }
                const nested = variantItem?.addons?.length ? variantItem.addons : item.addons;
                const nextPath = new Set(path);
                nextPath.add(item.id);
                if (variant) nextPath.add(variant.itemId);
                walk(nested, depth + 1, nextPath);
            }
        }
    };
    walk(context?.addons, 0, new Set());

    const byPackageItem = new Map<string, EnrichedPackageWithQuantity>();
    for (const pkg of collected) {
        const existing = byPackageItem.get(pkg.packageItemId);
        if (existing) existing.addonQuantity += pkg.addonQuantity;
        else byPackageItem.set(pkg.packageItemId, { ...pkg });
    }
    return [...byPackageItem.values()].filter((pkg) => pkg.addonQuantity !== 0);
};
